import { Box, CircularProgress, Divider, InputAdornment, TextField, Typography } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import React, { useState } from "react"
import type { FC, UIEvent } from "react"
import { useFormContext } from "react-hook-form"

import { FilterCheckbox } from "@/components/common/Filters"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"
import useProductManager from "@/hooks/api/useProductManager"
import useDebounce from "@/utils/useDebounce"

interface ProductManagerFilterProps {
  onFilterChange: (name: string, value: any) => void
}

export const ProductManagerFilter: FC<ProductManagerFilterProps> = ({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { control, watch } = useFormContext()
  const productManagersValue = watch("assignedToIds") || []
  const debouncedSearchString = useDebounce(searchTerm, DEBOUNCE_DELAY_MS)

  const { productManagerList, fetching, loadMore, hasMore } = useProductManager({
    query: debouncedSearchString,
    isAs400ProductManager: true,
  })

  const handleFilterChange = (newValue: string[]) => {
    onFilterChange("assignedToIds", newValue)
  }

  const canLoadMore = (element: UIEvent) => {
    const el = element.currentTarget
    return !fetching && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight
  }

  return (
    <Box className='flex h-full flex-col'>
      <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
        Assigned To
      </Typography>

      <Divider className='mb-4' />

      <Box className='flex-none p-4'>
        <TextField
          {...TEXT_FIELD_STYLING_PROPS}
          placeholder='Search by Name'
          value={searchTerm}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <Box className='flex grow flex-col overflow-hidden'>
        <Box
          className='h-[224px] grow overflow-y-auto overflow-x-hidden px-4 py-2'
          onScroll={(event) => {
            if (canLoadMore(event)) {
              loadMore()
            }
          }}
        >
          {productManagerList.map((productManager) => (
            <FilterCheckbox
              key={productManager.userId}
              label={`${productManager.name}`}
              value={productManager.userId}
              control={control}
              name='assignedToIds'
              values={productManagersValue}
              onFilterChange={handleFilterChange}
            />
          ))}

          {fetching && (
            <Box display='flex' justifyContent='center' py={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
